import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Menu, X, Phone, Mail, MapPin, User as UserIcon, Settings } from "lucide-react";
import { api, MODULES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import { BasketButton } from "@/components/BasketButton";
import { ThemeToggle } from "@/components/ThemeToggle";

const UPCOMING = ["PlantIntel", "End Use Industry Trends", "Export Volume", "Industry Analysis", "Market Share", "Trade Balance"];
export const OFFICES = ["HQ: Raigarh, CT 496445, IN", "Gachibowli, Hyderabad, Telangana 500019, IN", "Yalahanka, Bangalore, KA 560064, IN"];
export const CONTACT_EMAIL = "contact@ccm-intel.com";

function Dropdown({ label, items, testId, extra, wide }) {
  return (
    <div className="relative group" data-testid={testId}>
      <button className="flex items-center gap-1 px-3 py-2 text-sm text-slate-300 hover:text-white">{label}<ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" /></button>
      <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-50">
        <div className={`bg-slate-950/95 backdrop-blur-md border border-slate-800 rounded-lg p-2 shadow-2xl ${wide ? "w-[560px] grid grid-cols-2 gap-x-2 max-h-[70vh] overflow-y-auto" : "min-w-[240px]"}`}>
          {items.map((i) => <Link key={i.to} to={i.to} className="block px-3 py-2 text-sm rounded-md text-slate-300 hover:bg-[#1074BA]/15 hover:text-white">{i.label}</Link>)}
          {extra}
        </div>
      </div>
    </div>
  );
}

const linkCls = ({ isActive }) => `px-3 py-2 text-sm transition-colors ${isActive ? "text-white" : "text-slate-300 hover:text-white"}`;

export default function SiteLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { data: pages = [] } = useQuery({ queryKey: ["pages"], queryFn: async () => (await api.get("/pages")).data });
  const main = pages.filter((p) => p.menu === "main");
  const company = pages.filter((p) => p.menu === "company");
  const legal = pages.filter((p) => p.menu === "legal");
  const { data: cats = [] } = useQuery({ queryKey: ["report-cats"], queryFn: async () => (await api.get("/reports/categories")).data });
  const dash = MODULES.map((m) => ({ to: m.path, label: m.label }));
  const upcoming = (
    <div className="border-t border-slate-800 mt-2 pt-2">
      <p className="px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-[#F58B23]">Upcoming dashboards</p>
      {UPCOMING.map((u) => <p key={u} className="px-3 py-1 text-xs text-slate-500">{u}</p>)}
    </div>
  );

  return (
    <div className="min-h-screen grain relative flex flex-col">
      <div className="relative z-50 bg-[#1074BA] text-white text-xs" data-testid="site-topbar">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-9 flex items-center gap-6">
          <p className="truncate flex-1">CCMIntel Dashboard is your go-to source for comprehensive, real-time insights tailored to the petrochemicals and specialty chemicals industry.</p>
          <Link to="/about-us" className="hidden md:block hover:underline">About us</Link>
          <Link to="/contact" className="hidden md:block hover:underline">Contact us</Link>
          <a href="tel:+918977092264" className="hidden sm:flex items-center gap-1.5 font-mono"><Phone className="h-3 w-3" />+91 89770 92264</a>
        </div>
      </div>
      <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-950/85 border-b border-slate-800/80">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center gap-6">
          <Link to="/" data-testid="site-logo-link" className="shrink-0"><Logo className="h-9" /></Link>
          <nav className="hidden lg:flex items-center flex-1">
            <NavLink to="/" end className={linkCls} data-testid="site-nav-home">Home</NavLink>
            <Dropdown label="Solution Dashboard" items={[{ to: "/dashboards", label: "All Dashboards" }, ...dash]} extra={upcoming} testId="site-nav-dashboards" />
            <Dropdown label="Special Reports" wide items={[{ to: "/reports", label: "All Special Reports" }, ...cats.map((c) => ({ to: `/reports?category=${encodeURIComponent(c.name)}`, label: c.name }))]} testId="site-nav-reports" />
            {main.map((p) => <NavLink key={p.slug} to={`/${p.slug}`} className={linkCls} data-testid={`site-nav-${p.slug}`}>{p.title}</NavLink>)}
            <Dropdown label="Company" items={[...company.map((p) => ({ to: `/${p.slug}`, label: p.title })), { to: "/contact", label: "Contact Us" }]} testId="site-nav-company" />
          </nav>
          <div className="hidden lg:flex items-center gap-2 ml-auto">
            <ThemeToggle testId="site-theme-toggle" />
            <BasketButton testId="site-basket-button" />
            {user?.role === "admin" && <Link to="/admin" data-testid="site-nav-cms" className="p-2 text-slate-400 hover:text-white" title="CMS"><Settings className="h-4 w-4" /></Link>}
            {user ? (
              <>
                <Link to="/account" data-testid="site-nav-account" className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-300 hover:text-white"><UserIcon className="h-4 w-4" />{user.name}</Link>
                <button onClick={logout} data-testid="site-nav-logout" className="text-xs text-slate-500 hover:text-[#F58B23]">Logout</button>
              </>
            ) : (
              <Link to="/login" data-testid="site-nav-login" className="px-4 py-2 rounded-md bg-[#1074BA] hover:bg-[#0D629E] text-white text-sm font-medium">Login / Register</Link>
            )}
          </div>
          <span className="lg:hidden ml-auto flex items-center"><ThemeToggle testId="site-theme-toggle-mobile" /><BasketButton testId="site-basket-button-mobile" /></span>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="site-mobile-menu">{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-800 px-4 py-3 grid gap-1" onClick={() => setOpen(false)}>
            {[{ to: "/", label: "Home" }, { to: "/dashboards", label: "Dashboards" }, { to: "/reports", label: "Reports" }, ...main.map((p) => ({ to: `/${p.slug}`, label: p.title })),
              ...company.map((p) => ({ to: `/${p.slug}`, label: p.title })), { to: "/contact", label: "Contact" }, { to: user ? "/account" : "/login", label: user ? "My Account" : "Login" }].map((i) => (
              <Link key={i.to} to={i.to} className="py-2 text-sm text-slate-300">{i.label}</Link>
            ))}
          </div>
        )}
      </header>
      <main className="relative z-10 flex-1"><Outlet /></main>
      <footer className="relative z-10 border-t border-slate-800 bg-slate-950/80 mt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <Logo className="h-10" />
            <p className="text-sm text-slate-400 mt-4 max-w-md">Cognitive Chemical Market Intel — dashboards, special reports, consulting, technology transfer and plant transaction advisory for the chemical industry.</p>
            <div className="mt-5 space-y-2 text-sm text-slate-400" data-testid="footer-contact">
              {OFFICES.map((o) => <p key={o} className="flex gap-2"><MapPin className="h-4 w-4 text-[#F58B23] shrink-0 mt-0.5" />{o}</p>)}
              <a href={`mailto:${CONTACT_EMAIL}`} className="flex gap-2 hover:text-white"><Mail className="h-4 w-4 text-[#03AB9E]" />{CONTACT_EMAIL}</a>
              <a href="https://wa.me/918977092264" className="flex gap-2 hover:text-white" data-testid="footer-phone"><Phone className="h-4 w-4 text-[#03AB9E]" />+91 89770 92264</a>
            </div>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Explore</p>
            {[["/dashboards", "Dashboards"], ["/reports", "Reports"], ...main.map((p) => [`/${p.slug}`, p.title]), ["/subscribe", "Subscribe"], ["/contact", "Contact"]].map(([to, l]) => (
              <Link key={to} to={to} className="block text-sm text-slate-400 hover:text-white py-1">{l}</Link>
            ))}
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Company</p>
            {[...company.map((p) => [`/${p.slug}`, p.title]), ["/contact", "Contact Us"]].map(([to, l]) => <Link key={to} to={to} className="block text-sm text-slate-400 hover:text-white py-1">{l}</Link>)}
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Legal</p>
            {legal.map((p) => <Link key={p.slug} to={`/${p.slug}`} data-testid={`footer-${p.slug}`} className="block text-sm text-slate-400 hover:text-white py-1">{p.title}</Link>)}
          </div>
        </div>
        <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500 font-mono">© {new Date().getFullYear()} CCMIntel – Cognitive Chemical Market Intel. All rights reserved.</div>
      </footer>
    </div>
  );
}
